import * as Actions from "../redux/assessmentSession";
import * as OmniDataAPI from "../../data/omniDataAPI";
import * as PartnerSessionActions from "../redux/partnerSession";
import * as ProviderSessionActions from "../redux/providerSession";
import * as UserSessionStorage from "../../utils/userSessionStorage";

import { call, put, select, takeLatest } from "redux-saga/effects";

import { assessmentTemplates as hardcodedAssessments } from "../../assets/assessments";
import { isValidUUID } from "../../utils/helpers";
import { v4 as uuid } from "uuid";

export function* loadAssessments({ payload }) {
  try {
    //payload is a "pagination" from helpers.js

    let currentCacheInvalid = false;
    const pagination = yield select(Actions.assessmentPaginationSelector);
    if (
      payload.pageSize !== pagination.pageSize ||
      JSON.stringify(payload.query) !== JSON.stringify(pagination.query) ||
      JSON.stringify(payload.sort) !== JSON.stringify(pagination.sort) ||
      payload.invalidateCache
    ) {
      //something fundamental has changed
      currentCacheInvalid = true;
      payload.invalidateCache = false;
    }

    let assessmentPages = yield select(Actions.assessmentPagesSelector);
    if (!currentCacheInvalid && assessmentPages.hasOwnProperty(payload.pageIndex)) {
      yield put(
        Actions.loadAssessmentsSuccess({
          pages: assessmentPages,
          items: assessmentPages[payload.pageIndex].odms,
          pagination: payload,
        })
      );
    } else {
      const focus = yield select(Actions.assessmentPaginationFocusSelector);
      //move this out?
      payload.query["omniData.omniType"] = {
        "#regex": `${focus}`,
        "#options": "i",
      };

      let theReturn = yield call(OmniDataAPI.searchAssessments, payload);

      //upate the pagination
      payload.totalItems = theReturn.pageInfo.itemCount;

      if (currentCacheInvalid) {
        assessmentPages = {};
      }
      assessmentPages[payload.pageIndex] = { odms: theReturn.items };

      yield put(
        Actions.loadAssessmentsSuccess({
          pages: assessmentPages,
          items: assessmentPages[payload.pageIndex].odms,
          pagination: payload,
        })
      );
    }
  } catch (error) {
    yield put(Actions.assessmentSessionRequestFailed(error));
  }
}

export function* loadAssessmentODM({ payload }) {
  try {
    //clear out the existing information
    yield put(Actions.setAssessmentODMRequest(null));
    if (isValidUUID(payload)) {
      //payload is a valid UUID
      let assessmentODM = yield call(OmniDataAPI.getAssessment, payload);

      if (assessmentODM == null) {
        yield put(Actions.assessmentSessionRequestFailed("OmniDataAPI.getAssessment FAILED"));
      } else {
        yield put(Actions.setAssessmentODMRequest(assessmentODM));
        yield put(Actions.loadAssessmentODMSuccess());
      }
    } else {
      yield put(Actions.assessmentSessionRequestFailed("Bad Assessment _id"));
    }
  } catch (error) {
    yield put(Actions.assessmentSessionRequestFailed(error));
  }
}

export function* loadAssessmentTemplates({ payload }) {
  try {
    let theResult = hardcodedAssessments;

    //TODO inject valid hardcoded templates
    yield put(Actions.loadAssessmentTemplatesSuccess(theResult));
  } catch (error) {
    yield put(Actions.assessmentSessionRequestFailed(error));
  }
}

export function* updateAssessment({ payload }) {
  try {
    if (payload._id == null) {
      //CREATE
      payload._id = uuid();
      let newAssessment = yield call(OmniDataAPI.createAssessment, payload);

      if (newAssessment == null) {
        yield put(Actions.assessmentSessionRequestFailed("OmniDataAPI.createAssessment FAILED"));
      } else {
        yield put(Actions.setAssessmentODMRequest(newAssessment));
        yield put(Actions.updateAssessmentSuccess());
        yield put(PartnerSessionActions.reflectUpdatedAssessmentODMRequest(newAssessment));
        yield put(ProviderSessionActions.reflectUpdatedAssessmentODMRequest(newAssessment));
      }
    } else if (isValidUUID(payload._id)) {
      //UPDATE
      let newAssessment = yield call(OmniDataAPI.updateAssessment, payload);
      if (newAssessment == null) {
        yield put(Actions.assessmentSessionRequestFailed("OmniDataAPI.updateAssessment FAILED"));
      } else {
        yield put(Actions.setAssessmentODMRequest(newAssessment));

        yield put(Actions.updateAssessmentSuccess());
        yield put(ProviderSessionActions.reflectUpdatedAssessmentODMRequest(newAssessment));
      }
    } else {
      yield put(Actions.assessmentSessionRequestFailed("updateAssessment: bad payload"));
    }
  } catch (error) {
    yield put(Actions.assessmentSessionRequestFailed(error));
  }
}

export function* setAssessmentODM({ payload }) {
  try {
    //Validate Object
    //Do all ancillary data loads here

    yield put(Actions.setAssessmentODMSuccess(payload));
  } catch (error) {
    yield put(Actions.assessmentSessionRequestFailed(error));
  }
}

export function* setAssessmentTemplateODM({ payload }) {
  try {
    //Validate Object
    //Do all ancillary data loads here

    yield put(Actions.setAssessmentTemplateODMSuccess(payload));
  } catch (error) {
    yield put(Actions.assessmentSessionRequestFailed(error));
  }
}

export function* deleteAssessment({ payload }) {
  try {
    //payload is UUID
    if (isValidUUID(payload)) {
      let assessment = yield call(OmniDataAPI.deleteAssessment, payload);
      if (assessment != null) {
        yield put(Actions.deleteAssessmentSuccess());
        //do the reflect calls.
        yield put(PartnerSessionActions.reflectUpdatedAssessmentODMRequest(payload));
        yield put(ProviderSessionActions.reflectUpdatedAssessmentODMRequest(payload));
      } else {
        yield put(Actions.assessmentSessionRequestFailed("deleteAssessment: call failed"));
      }
    } else {
      yield put(Actions.assessmentSessionRequestFailed("deleteAssessment: invalid UUID()"));
    }
  } catch (error) {
    yield put(Actions.assessmentSessionRequestFailed(error));
  }
}

export function* setAssessmentPaginationFocus({ payload }) {
  try {
    yield call(UserSessionStorage.storeLastAssessmentType, payload);
    yield put(Actions.setAssessmentPaginationFocusSuccess(payload));
  } catch (error) {
    yield put(Actions.assessmentSessionRequestFailed(error));
  }
}

//  saga
export default function* assessmentDataSaga() {
  yield takeLatest(Actions.loadAssessmentsRequest, loadAssessments);
  yield takeLatest(Actions.loadAssessmentODMRequest, loadAssessmentODM);
  yield takeLatest(Actions.loadAssessmentTemplatesRequest, loadAssessmentTemplates);
  yield takeLatest(Actions.setAssessmentODMRequest, setAssessmentODM);
  yield takeLatest(Actions.setAssessmentTemplateODMRequest, setAssessmentTemplateODM);
  yield takeLatest(Actions.updateAssessmentRequest, updateAssessment);
  yield takeLatest(Actions.deleteAssessmentRequest, deleteAssessment);
  yield takeLatest(Actions.setAssessmentPaginationFocusRequest, setAssessmentPaginationFocus);
}
